import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Grid, Typography, Paper, Chip, Divider,
    FormControl, InputLabel, Select, MenuItem, Menu,
    CircularProgress, Alert, Stack, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Avatar
} from '@mui/material'; // Agregamos Collapse
import { Collapse, IconButton, Box as MuiBox } from '@mui/material'; // Importamos Collapse y IconButton
import {
    Timeline, TimelineItem, TimelineSeparator,
    TimelineConnector, TimelineContent, TimelineDot,
    TimelineOppositeContent
} from '@mui/lab';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ticketService, configService, userService } from '../api';
import AppDialogTitle from './AppDialogTitle';

const priorityColors = {
    HIGH: 'error',
    MEDIUM: 'warning',
    LOW: 'success',
};

const fieldLabels = {
    status: 'Estado',
    priority: 'Prioridad',
    assigned_to: 'Agente Asignado',
    title: 'Asunto',
};

const TicketDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const userRole = localStorage.getItem('role');
    const [ticket, setTicket] = useState(null);
    const [currentUserEmail, setCurrentUserEmail] = useState('');
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [noteError, setNoteError] = useState(null); // Nuevo estado para errores de notas
    const [newNote, setNewNote] = useState('');
    const [isNoteSubmitting, setIsNoteSubmitting] = useState(false);
    const [pendingStatus, setPendingStatus] = useState('');
    const [pendingPriority, setPendingPriority] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [successMsg, setSuccessMessage] = useState('');
    const [mentionAnchor, setMentionAnchor] = useState(null);
    const [mentionFilter, setMentionFilter] = useState('');
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailData, setEmailData] = useState({ subject: '', message: '' });
    const [isEmailSending, setIsEmailSending] = useState(false);
    const [agents, setAgents] = useState([]);
    const [reassignAnchorEl, setReassignAnchorEl] = useState(null);
    const [notesExpanded, setNotesExpanded] = useState(true); // Estado para colapsar/expandir notas
    const [timeLeft, setTimeLeft] = useState(''); // Estado para el cronómetro
    const [publicMessage, setPublicMessage] = useState('');
    const [isMessageSubmitting, setIsMessageSubmitting] = useState(false);

    // Lógica del cronómetro del SLA
    useEffect(() => {
        if (!ticket?.expected_resolution_date || ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') {
            setTimeLeft('');
            return;
        }

        const updateTimer = () => {
            const now = new Date();
            const target = new Date(ticket.expected_resolution_date);
            const diff = target - now;

            if (diff <= 0) {
                setTimeLeft('VENCIDO');
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
        };

        const timerId = setInterval(updateTimer, 1000);
        updateTimer(); // Ejecución inicial

        return () => clearInterval(timerId);
    }, [ticket?.expected_resolution_date, ticket?.status]);

    const fetchTicket = useCallback(async () => {
        try {
            const response = await ticketService.getTicket(id);
            setTicket(response.data);
            setEmailData(prev => ({ ...prev, subject: `Actualización de Ticket #${response.data.id}: ${response.data.title}` }));
            setPendingPriority(response.data.priority);
            setPendingStatus(response.data.status);
        } catch (err) {
            console.error("Detalle del error al cargar ticket:", err.response?.data || err.message);
            const msg = err.response?.data?.detail || err.message || "Error desconocido";
            setError(`Error al cargar los detalles: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchTicket();
        const loadDependencies = async () => {
            try {
                const res = await ticketService.getAgents();
                setAgents(res.data.results || res.data);
                const statusRes = await configService.getStatuses();
                setStatuses(statusRes.data.results || statusRes.data);
                
                const profileRes = await userService.getProfile();
                setCurrentUserEmail(profileRes.data.email);
            } catch (err) {
                console.error("Error fetching dependencies:", err);
            }
        };
        loadDependencies();
    }, [fetchTicket]);

    const handleStatusChange = (event) => {
        setPendingStatus(event.target.value);
    };

    const handlePriorityChange = (event) => {
        setPendingPriority(event.target.value);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Enviamos tanto el estado como la prioridad en una sola petición PATCH
            await ticketService.updateTicket(id, { status: pendingStatus, priority: pendingPriority });
            setSuccessMessage(t('ticket_update_success'));
            await fetchTicket(); // Recargar para ver el historial actualizado
        } catch (err) {
            alert("Error al guardar los cambios");
            // Si falla, volvemos a los valores originales del ticket
            setPendingStatus(ticket.status);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAgentSelect = async (agentId) => {
        setReassignAnchorEl(null);
        setIsSaving(true);
        try {
            await ticketService.updateTicket(id, { assigned_to: agentId });
            await fetchTicket(); // Recargar para ver el cambio y el historial
        } catch (err) {
            alert("Error al reasignar el ticket");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar esta nota?")) return;
        try {
            await ticketService.deleteInternalNote(noteId);
            setSuccessMessage("Nota eliminada correctamente");
            await fetchTicket();
        } catch (err) {
            console.error("Error al eliminar la nota:", err);
            alert("No se pudo eliminar la nota.");
        }
    };

    const handleNoteChange = (e) => {
        const value = e.target.value;
        setNewNote(value);

        // Lógica de detección de mención (@)
        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);
        const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

        if (lastAtSymbol !== -1 && !/\s/.test(textBeforeCursor.substring(lastAtSymbol + 1))) {
            setMentionFilter(textBeforeCursor.substring(lastAtSymbol + 1));
            setMentionAnchor(e.currentTarget);
        } else {
            setMentionAnchor(null);
        }
    };

    const handleAddPublicMessage = async () => {
        if (!publicMessage.trim()) return;
        setIsMessageSubmitting(true);
        try {
            await ticketService.addPublicMessage(id, publicMessage);
            setPublicMessage('');
            await fetchTicket();
        } catch (err) {
            alert("Error al enviar el mensaje");
        } finally {
            setIsMessageSubmitting(false);
        }
    };

    const insertMention = (email) => {
        const lastAt = newNote.lastIndexOf('@');
        const prefix = newNote.substring(0, lastAt);
        setNewNote(prefix + '@' + email + ' ');
        setMentionAnchor(null);
    };

    const handleSendEmail = async () => {
        if (!emailData.message.trim()) return;
        setIsEmailSending(true);
        try {
            await ticketService.sendEmail(id, emailData);
            setEmailDialogOpen(false);
            setEmailData({ ...emailData, message: '' });
            setSuccessMessage(t('ticket_email_success'));
        } catch (err) {
            alert("Error al enviar el email");
        } finally {
            setIsEmailSending(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;
        setIsNoteSubmitting(true);
        setNoteError(null); // Limpiar errores previos de notas
        try {
            await ticketService.addInternalNote(id, newNote);
            setNewNote('');
            setSuccessMessage(t('ticket_note_success'));
            await fetchTicket(); // Recargar para ver la nota nueva en la lista y trazabilidad
        } catch (err) {
            console.error("Error al guardar la nota interna:", err.response?.data || err.message);
            let errorMessage = "Error desconocido al guardar la nota.";
            if (err.response?.data) {
                // Verificar errores específicos del backend
                if (err.response.data.note) {
                    errorMessage = err.response.data.note[0];
                } else if (err.response.data.ticket) {
                    errorMessage = err.response.data.ticket[0];
                } else if (err.response.data.detail) {
                    errorMessage = err.response.data.detail;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                } else {
                    errorMessage = "Error al guardar la nota interna. Verifique los datos.";
                }
            }
            setNoteError(errorMessage);
        } finally {
            setIsNoteSubmitting(false);
        }
    };

    // Combinar historial, notas y creación en una sola línea de tiempo
    const combinedTimeline = useMemo(() => {
        if (!ticket) return [];

        const items = [
            {
                id: 'creation',
                type: 'creation',
                date: ticket.created_at,
                user: ticket.submitter_email,
                content: 'Creó el ticket'
            },
            ...(ticket.history || []).map(h => ({
                id: `h-${h.id}`,
                type: 'history',
                date: h.changed_at,
                user: h.changed_by_email,
                field: h.field_changed,
                old: h.old_value,
                new: h.new_value
            })),
            ...(ticket.internal_notes || []).map(n => ({
                id: `n-${n.id}`,
                type: 'note',
                date: n.created_at,
                user: n.author_name,
                content: n.note,
                avatar: n.author_avatar
            }))
        ];

        // Ordenar de más reciente a más antiguo (para que la creación quede al final o al principio según prefieras)
        return items.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [ticket]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!ticket) return <Alert severity="warning">Ticket no encontrado</Alert>;

    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/tickets")}
          >
            Volver
          </Button>
        </Stack>

        {/* Barra de Acciones Rápidas */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            gutterBottom
            color="textSecondary"
            fontWeight="bold"
            sx={{ mb: 1.5 }}
          >
            Acciones Rápidas
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<EventIcon />}
              color="primary"
              size="small"
            >
              Modificar Resolución
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              color="primary"
              size="small"
              onClick={(e) => setReassignAnchorEl(e.currentTarget)}
            >
              Reasignar
            </Button>
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              color="primary"
              size="small"
              onClick={() => setEmailDialogOpen(true)}
            >
              Enviar Email
            </Button>
          </Stack>
          <Menu
            anchorEl={reassignAnchorEl}
            open={Boolean(reassignAnchorEl)}
            onClose={() => setReassignAnchorEl(null)}
          >
            {agents.map((agent) => (
              <MenuItem
                key={agent.id}
                onClick={() => handleAgentSelect(agent.id)}
              >
                {agent.full_name} ({agent.email})
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Grid container spacing={3}>
          {/* Columna Izquierda: Información del Ticket y Notas */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography
                variant="overline"
                color="textSecondary"
                sx={{ display: "block", lineHeight: 1 }}
              >
                Ticket #{ticket.id}
              </Typography>
              <Typography
                variant="h4"
                gutterBottom
                fontWeight="bold"
                sx={{ fontSize: "20px" }}
              >
                {ticket.title}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mb: 3, fontSize: "14px" }}
              >
                {ticket.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Estado
                  </Typography>
                  <Box>
                    <Chip
                      label={
                        statuses.find((s) => s.key === ticket.status)?.name ||
                        ticket.status
                      }
                      sx={{
                        bgcolor:
                          statuses.find((s) => s.key === ticket.status)
                            ?.color || "#757575",
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Prioridad
                  </Typography>
                  <Box>
                    <Chip
                      label={ticket.priority}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Remitente</Typography>
                  <Typography variant="body2">
                    {ticket.submitter_email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Asignado a</Typography>
                  <Typography variant="body2">
                    {ticket.assigned_to_email || "Sin Asignar"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Fecha de Creación</Typography>
                  <Typography variant="body2">
                    {new Date(ticket.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">
                    Resolución Esperada
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    {new Date(ticket.expected_resolution_date).toLocaleString()}
                  </Typography>
                </Grid>
                {timeLeft && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">Tiempo Restante (SLA)</Typography>
                    <Typography variant="body2" fontWeight="bold" color={timeLeft === 'VENCIDO' ? 'error.main' : 'primary.main'}>
                      {timeLeft}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {ticket.attachments && ticket.attachments.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Adjuntos
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {ticket.attachments.map((att) => (
                      <Chip
                        key={att.id}
                        label={att.file.split("/").pop()}
                        component="a"
                        href={att.file}
                        target="_blank"
                        clickable
                        icon={<AttachFileIcon />}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>

            {/* Chat Público con el Usuario */}
            <Paper sx={{ p: 3, mb: 3, borderLeft: '5px solid #742DEF' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    {t('ticket_chat_title')}
                </Typography>
                <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2, p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {ticket.messages?.map((msg) => (
                        <Box 
                            key={msg.id} 
                            sx={{ 
                                alignSelf: msg.is_agent ? 'flex-end' : 'flex-start',
                                maxWidth: '80%',
                                bgcolor: msg.is_agent ? 'primary.main' : 'grey.200',
                                color: msg.is_agent ? 'white' : 'text.primary',
                                p: 1.5,
                                borderRadius: msg.is_agent ? '15px 15px 2px 15px' : '15px 15px 15px 2px',
                                boxShadow: 1
                            }}
                        >
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', opacity: 0.8 }}>
                                {msg.sender_name}
                            </Typography>
                            <Typography variant="body2">{msg.message}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.65rem' }}>
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Stack direction="row" spacing={1}>
                    <TextField 
                        fullWidth 
                        size="small" 
                        placeholder={t('ticket_message_placeholder')} 
                        value={publicMessage}
                        onChange={(e) => setPublicMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddPublicMessage()}
                    />
                    <Button 
                        variant="contained" 
                        onClick={handleAddPublicMessage}
                        disabled={isMessageSubmitting || !publicMessage.trim()}
                    >
                        {isMessageSubmitting ? <CircularProgress size={20} /> : t('ticket_message_send')}
                    </Button>
                </Stack>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: "#EFEFEF" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#5d4037", fontWeight: "bold" }}
                >
                  Notas Internas (Solo Agentes)
                </Typography>
                <IconButton
                  onClick={() => setNotesExpanded(!notesExpanded)}
                  size="small"
                >
                  {notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Collapse in={notesExpanded}>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {noteError && <Alert severity="error">{noteError}</Alert>}{" "}
                  {/* Mostrar error específico de notas */}
                  {ticket.internal_notes?.map((note) => (
                    <Box
                      key={note.id}
                      sx={{
                        p: 1.5,
                        bgcolor: "rgba(0,0,0,0.03)",
                        borderRadius: 2,
                        '&:hover .delete-note-btn': { opacity: 1 }
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <Avatar
                          src={
                            note.author_avatar
                              ? note.author_avatar.startsWith("http")
                                ? note.author_avatar
                                : `http://${window.location.hostname}:8000${note.author_avatar}`
                              : undefined
                          }
                          sx={{ width: 32, height: 32, mt: 0.5 }}
                        >
                          {note.author_name.charAt(0)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "#bf360c", fontWeight: "bold" }}
                          >
                            {note.author_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ my: 0.5, whiteSpace: "pre-wrap" }}
                          >
                            {note.note}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(note.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        {(userRole === 'ADMIN' || note.author_email === currentUserEmail) && (
                            <IconButton
                                className="delete-note-btn"
                                size="small"
                                color="error"
                                onClick={() => handleDeleteNote(note.id)}
                                sx={{ 
                                    ml: 1, 
                                    opacity: 0, 
                                    transition: 'opacity 0.2s ease-in-out' 
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Escribe una nota privada para otros agentes..."
                  value={newNote}
                  onChange={handleNoteChange}
                  variant="filled"
                  size="small"
                  sx={{ mb: 1, bgcolor: "white" }}
                />

                {/* Menú de Sugerencias de Menciones */}
                <Menu
                  anchorEl={mentionAnchor}
                  open={Boolean(mentionAnchor)}
                  onClose={() => setMentionAnchor(null)}
                  disableAutoFocusItem
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <MenuItem
                    disabled
                    sx={{ fontSize: "0.75rem", fontWeight: "bold" }}
                  >
                    MENCIONAR A:
                  </MenuItem>
                  {agents
                    .filter(
                      (a) =>
                        a.full_name
                          ?.toLowerCase()
                          .includes(mentionFilter.toLowerCase()) ||
                        a.email
                          .toLowerCase()
                          .includes(mentionFilter.toLowerCase()),
                    )
                    .map((agent) => (
                      <MenuItem
                        key={agent.id}
                        onClick={() => insertMention(agent.email)}
                      >
                        <Avatar
                          src={agent.avatar}
                          sx={{ width: 24, height: 24, mr: 1 }}
                        />
                        {agent.full_name}
                      </MenuItem>
                    ))}
                </Menu>

                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleAddNote}
                  disabled={isNoteSubmitting || !newNote.trim()}
                >
                  {isNoteSubmitting ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Añadir Nota"
                  )}
                </Button>
              </Collapse>
            </Paper>
          </Grid>

          {/* Columna Derecha: Gestión y Trazabilidad */}
          <Grid item xs={12} md={4}>
            {/* Sección de Gestión */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Gestión
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Actualizar Estado</InputLabel>
                <Select
                  value={pendingStatus}
                  label="Actualizar Estado"
                  onChange={handleStatusChange}
                >
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.key}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Actualizar Prioridad</InputLabel>
                <Select
                  value={pendingPriority}
                  label="Actualizar Prioridad"
                  onChange={handlePriorityChange}
                >
                  <MenuItem value="LOW">Baja</MenuItem>
                  <MenuItem value="MEDIUM">Media</MenuItem>
                  <MenuItem value="HIGH">Alta</MenuItem>
                  <MenuItem value="CRITICAL">Crítica</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleSave}
                disabled={
                  isSaving ||
                  (pendingStatus === ticket.status &&
                    pendingPriority === ticket.priority)
                }
              >
                {isSaving ? <CircularProgress size={24} /> : "Guardar Cambios"}
              </Button>
            </Paper>

            {/* Sección de Trazabilidad */}
            <Paper sx={{ p: 3, minHeight: 400 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <HistoryIcon />
                <Typography variant="h6">Trazabilidad</Typography>
              </Stack>

              <Timeline position="right" sx={{ p: 0 }}>
                {combinedTimeline.map((item, index) => (
                  <TimelineItem key={item.id}>
                    <TimelineOppositeContent
                      color="textSecondary"
                      sx={{ fontSize: "0.75rem", flex: 0.2 }}
                    >
                      {new Date(item.date).toLocaleString([], {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot
                        color={
                          item.type === "creation"
                            ? "success"
                            : item.type === "note"
                              ? "warning"
                              : "primary"
                        }
                        variant="outlined"
                      />
                      {index < combinedTimeline.length - 1 && (
                        <TimelineConnector />
                      )}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: "12px", px: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={0.5}
                      >
                        {item.type === "note" && (
                          <Avatar
                            src={
                              item.avatar
                                ? item.avatar.startsWith("http")
                                  ? item.avatar
                                  : `http://${window.location.hostname}:8000${item.avatar}`
                                : undefined
                            }
                            sx={{ width: 20, height: 20, fontSize: "0.7rem" }}
                          >
                            {item.user.charAt(0)}
                          </Avatar>
                        )}
                        <Typography variant="subtitle2" component="span">
                          {item.user}
                        </Typography>
                      </Stack>

                      {item.type === "history" ? (
                        <>
                          <Typography variant="body2" color="textSecondary">
                            Actualizó{" "}
                            <strong>
                              {fieldLabels[item.field] || item.field}
                            </strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", fontStyle: "italic" }}
                          >
                            {item.old || "Ninguno"} → {item.new}
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            fontStyle:
                              item.type === "note" ? "italic" : "normal",
                          }}
                        >
                          {item.content}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Paper>
          </Grid>
        </Grid>

        {/* Diálogo para Enviar Email */}
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <AppDialogTitle onClose={() => setEmailDialogOpen(false)}>
            Enviar Email al Usuario
          </AppDialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Asunto"
              value={emailData.subject}
              onChange={(e) =>
                setEmailData({ ...emailData, subject: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Mensaje"
              multiline
              rows={6}
              value={emailData.message}
              onChange={(e) =>
                setEmailData({ ...emailData, message: e.target.value })
              }
              margin="normal"
              placeholder="Escribe tu respuesta para el usuario..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSendEmail}
              variant="contained"
              disabled={isEmailSending || !emailData.message.trim()}
            >
              {isEmailSending ? <CircularProgress size={24} /> : "Enviar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
};

export default TicketDetail;